import React, { useState, useEffect, useMemo } from 'react';
import { 
  Activity, 
  TrendingUp, 
  AlertTriangle, 
  Users, 
  Clock, 
  Target, 
  BarChart3,
  Network,
  Zap,
  Brain,
  Search,
  Filter,
  Settings,
  Download,
  Play,
  Pause,
  CheckCircle,
  Loader
} from 'lucide-react';

import { 
  EventLog, 
  ProcessModel, 
  ConformanceResult, 
  PerformanceMetrics, 
  Alert,
  ProcessDiscoveryAlgorithm,
  ActivityType,
  TrendDirection,
  AlertPriority
} from '../types/processMining';

import { ProcessMiningEngine } from '../utils/processMining';

// ============================================================================
// INTERFACES LOCAIS
// ============================================================================

interface ProcessMiningDashboardProps {
  eventLogs: EventLog[];
  onAlgorithmChange?: (algorithm: ProcessDiscoveryAlgorithm) => void;
  onFilterChange?: (filters: any) => void;
}

interface ProcessDiscoveryConfig {
  algorithm: ProcessDiscoveryAlgorithm;
  parameters: {
    threshold: number;
    noise_level: number;
    complexity_reduction: boolean;
  };
}

interface KPICard {
  title: string;
  value: string | number;
  change: number;
  trend: TrendDirection;
  icon: React.ComponentType<any>;
  color: string;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const ProcessMiningDashboard: React.FC<ProcessMiningDashboardProps> = ({
  eventLogs,
  onAlgorithmChange,
  onFilterChange
}) => {
  // Estados
  const [selectedTab, setSelectedTab] = useState<'discovery' | 'conformance' | 'performance' | 'prediction'>('discovery');
  const [discoveryConfig, setDiscoveryConfig] = useState<ProcessDiscoveryConfig>({
    algorithm: ProcessDiscoveryAlgorithm.inductive_miner,
    parameters: {
      threshold: 0.8,
      noise_level: 0.2,
      complexity_reduction: true
    }
  });
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [processModel, setProcessModel] = useState<ProcessModel | null>(null);
  const [conformanceResults, setConformanceResults] = useState<ConformanceResult[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // Dados simulados para demonstração
  const mockKPIs: KPICard[] = [
    {
      title: 'Taxa de Conformidade',
      value: '87.5%',
      change: 2.3,
      trend: TrendDirection.improving,
      icon: Target,
      color: 'bg-green-500'
    },
    {
      title: 'Tempo Médio Ciclo',
      value: '4.2h',
      change: -8.1,
      trend: TrendDirection.improving,
      icon: Clock,
      color: 'bg-blue-500'
    },
    {
      title: 'Casos Ativos',
      value: '156',
      change: 12.5,
      trend: TrendDirection.stable,
      icon: Activity,
      color: 'bg-purple-500'
    },
    {
      title: 'Alertas Críticos',
      value: '3',
      change: -25.0,
      trend: TrendDirection.improving,
      icon: AlertTriangle,
      color: 'bg-red-500'
    }
  ];

  const mockAlerts: Alert[] = [
    {
      id: '1',
      type: 'compliance_violation' as any,
      priority: AlertPriority.high,
      title: 'Violação de Protocolo - UTI',
      description: 'Paciente com dor severa sem reavaliação em 2h',
      source: { system: 'process_mining', component: 'conformance_checker' },
      timestamp: new Date(),
      status: 'new' as any,
      escalation_level: 2,
      related_entities: [
        { type: 'patient', id: 'P001', name: 'João Silva' },
        { type: 'case', id: 'C001', name: 'Caso Dor Pós-Op' }
      ],
      actions_taken: []
    },
    {
      id: '2',
      type: 'performance_degradation' as any,
      priority: AlertPriority.medium,
      title: 'Gargalo Identificado - Enfermaria',
      description: 'Tempo de espera aumentou 40% na última semana',
      source: { system: 'process_mining', component: 'performance_analyzer' },
      timestamp: new Date(Date.now() - 3600000),
      status: 'acknowledged' as any,
      escalation_level: 1,
      related_entities: [
        { type: 'resource', id: 'ENF_MAT', name: 'Enfermagem Matutina' }
      ],
      actions_taken: [
        {
          timestamp: new Date(Date.now() - 1800000),
          user: 'Ana Costa',
          action: 'Investigação iniciada',
          description: 'Análise detalhada do gargalo'
        }
      ]
    }
  ];

  // Efeito para simular dados em tempo real
  useEffect(() => {
    if (isRealTimeActive) {
      const interval = setInterval(() => {
        // Simular atualização de dados em tempo real
        setAlerts(prev => [...prev, ...mockAlerts.slice(0, 1)]);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isRealTimeActive]);

  // Cálculos derivados
  const totalCases = useMemo(() => {
    return new Set(eventLogs.map(log => log.case_id)).size;
  }, [eventLogs]);

  const uniqueActivities = useMemo(() => {
    return new Set(eventLogs.map(log => log.activity)).size;
  }, [eventLogs]);

  // Função para executar descoberta de processo
  const executeProcessDiscovery = async () => {
    setIsProcessing(true);
    setProcessingMessage('Iniciando descoberta de processo...');
    
    try {
      // Simular delay para mostrar o processamento
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Executar algoritmo selecionado usando métodos estáticos
      setProcessingMessage(`Executando ${discoveryConfig.algorithm}...`);
      const discoveredModel = ProcessMiningEngine.discoverProcess(
        eventLogs,
        discoveryConfig.algorithm,
        discoveryConfig.parameters
      );
      
      setProcessModel(discoveredModel);
      
      // Simular análise de performance
      setProcessingMessage('Analisando performance...');
      await new Promise(resolve => setTimeout(resolve, 300));
      const performanceResults = ProcessMiningEngine.analyzePerformance(eventLogs);
      setPerformanceMetrics(performanceResults);
      
      setProcessingMessage('Descoberta concluída com sucesso!');
      
      // Mostrar notificação de sucesso
      setTimeout(() => {
        setProcessingMessage('');
        setIsProcessing(false);
      }, 1000);
      
    } catch (error) {
      console.error('Erro na descoberta de processo:', error);
      setProcessingMessage('Erro na descoberta de processo');
      setTimeout(() => {
        setProcessingMessage('');
        setIsProcessing(false);
      }, 2000);
    }
  };

  // ============================================================================
  // COMPONENTES DE RENDERIZAÇÃO
  // ============================================================================

  const renderKPICard = (kpi: KPICard, index: number) => (
    <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{kpi.value}</p>
        </div>
        <div className={`p-3 rounded-lg ${kpi.color}`}>
          <kpi.icon className="h-6 w-6 text-white" />
        </div>
      </div>
      <div className="mt-4 flex items-center">
        <TrendingUp 
          className={`h-4 w-4 mr-1 ${
            kpi.trend === TrendDirection.improving ? 'text-green-500' : 
            kpi.trend === TrendDirection.deteriorating ? 'text-red-500' : 
            'text-gray-500'
          }`} 
        />
        <span 
          className={`text-sm font-medium ${
            kpi.change > 0 ? 'text-green-600' : 
            kpi.change < 0 ? 'text-red-600' : 
            'text-gray-600'
          }`}
        >
          {kpi.change > 0 ? '+' : ''}{kpi.change}%
        </span>
        <span className="text-sm text-gray-500 ml-2">vs. semana anterior</span>
      </div>
    </div>
  );

  const renderProcessDiscoveryPanel = () => (
    <div className="space-y-6">
      {/* Configuração do Algoritmo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <Network className="h-5 w-5 inline-block mr-2" />
          Descoberta de Processo
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Algoritmo
            </label>
            <select 
              value={discoveryConfig.algorithm}
              onChange={(e) => {
                const newConfig = {
                  ...discoveryConfig,
                  algorithm: e.target.value as ProcessDiscoveryAlgorithm
                };
                setDiscoveryConfig(newConfig);
                onAlgorithmChange?.(newConfig.algorithm);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={ProcessDiscoveryAlgorithm.alpha_miner}>Alpha Miner</option>
              <option value={ProcessDiscoveryAlgorithm.inductive_miner}>Inductive Miner</option>
              <option value={ProcessDiscoveryAlgorithm.heuristics_miner}>Heuristics Miner</option>
              <option value={ProcessDiscoveryAlgorithm.fuzzy_miner}>Fuzzy Miner</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Threshold ({discoveryConfig.parameters.threshold})
            </label>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={discoveryConfig.parameters.threshold}
              onChange={(e) => setDiscoveryConfig(prev => ({
                ...prev,
                parameters: {
                  ...prev.parameters,
                  threshold: parseFloat(e.target.value)
                }
              }))}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nível de Ruído ({discoveryConfig.parameters.noise_level})
            </label>
            <input
              type="range"
              min="0.0"
              max="0.5"
              step="0.1"
              value={discoveryConfig.parameters.noise_level}
              onChange={(e) => setDiscoveryConfig(prev => ({
                ...prev,
                parameters: {
                  ...prev.parameters,
                  noise_level: parseFloat(e.target.value)
                }
              }))}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={discoveryConfig.parameters.complexity_reduction}
                onChange={(e) => setDiscoveryConfig(prev => ({
                  ...prev,
                  parameters: {
                    ...prev.parameters,
                    complexity_reduction: e.target.checked
                  }
                }))}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Redução de Complexidade</span>
            </label>
          </div>
          
          <button 
            onClick={executeProcessDiscovery}
            disabled={isProcessing || eventLogs.length === 0}
            className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
              isProcessing || eventLogs.length === 0
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isProcessing ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Executar Descoberta
              </>
            )}
          </button>
        </div>
        
        {/* Indicador de Processamento */}
        {isProcessing && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Loader className="h-5 w-5 text-blue-600 animate-spin mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">{processingMessage}</p>
                <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Visualização do Modelo de Processo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Modelo de Processo Descoberto
        </h3>
        
        {/* Placeholder para visualização do processo */}
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg h-96 flex items-center justify-center">
          <div className="text-center">
            <Network className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">Modelo de Processo</p>
            <p className="text-gray-400 text-sm">
              Execute a descoberta para visualizar o modelo
            </p>
          </div>
        </div>
        
        {/* Métricas de Qualidade do Modelo */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <p className="text-sm text-gray-600">Fitness</p>
            <p className="text-lg font-semibold text-gray-900">0.87</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <p className="text-sm text-gray-600">Precision</p>
            <p className="text-lg font-semibold text-gray-900">0.92</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <p className="text-sm text-gray-600">Generalização</p>
            <p className="text-lg font-semibold text-gray-900">0.84</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <p className="text-sm text-gray-600">Simplicidade</p>
            <p className="text-lg font-semibold text-gray-900">0.78</p>
          </div>
        </div>
      </div>

      {/* Análise de Variantes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Variantes de Processo
        </h3>
        
        <div className="space-y-3">
          {[
            { 
              id: 1, 
              sequence: ['Avaliação Inicial', 'Administração Medicamento', 'Reavaliação', 'Alta'],
              frequency: 45,
              percentage: 32.1,
              avgDuration: '3.2h'
            },
            {
              id: 2,
              sequence: ['Avaliação Inicial', 'Fisioterapia', 'Reavaliação', 'Medicamento', 'Alta'],
              frequency: 28,
              percentage: 20.0,
              avgDuration: '4.8h'
            },
            {
              id: 3,
              sequence: ['Avaliação Inicial', 'Medicamento', 'Escalation', 'Especialista', 'Alta'],
              frequency: 22,
              percentage: 15.7,
              avgDuration: '6.1h'
            }
          ].map((variant) => (
            <div key={variant.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-gray-900">Variante {variant.id}</h4>
                <div className="text-right">
                  <span className="text-sm text-gray-600">{variant.frequency} casos</span>
                  <span className="ml-2 text-sm font-medium text-blue-600">{variant.percentage}%</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mb-2">
                {variant.sequence.map((step, index) => (
                  <React.Fragment key={index}>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {step}
                    </div>
                    {index < variant.sequence.length - 1 && (
                      <span className="text-gray-400">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              <p className="text-sm text-gray-600">
                Duração média: <span className="font-medium">{variant.avgDuration}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConformancePanel = () => (
    <div className="space-y-6">
      {/* Status de Conformidade */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Taxa de Conformidade</h3>
            <Target className="h-6 w-6 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600 mb-2">87.5%</div>
          <p className="text-sm text-gray-600">+2.3% vs. semana anterior</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Violações Ativas</h3>
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600 mb-2">12</div>
          <p className="text-sm text-gray-600">-5 vs. semana anterior</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Casos em Análise</h3>
            <Users className="h-6 w-6 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600 mb-2">156</div>
          <p className="text-sm text-gray-600">Em monitoramento ativo</p>
        </div>
      </div>

      {/* Violações Recentes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Violações de Conformidade Recentes
        </h3>
        
        <div className="space-y-4">
          {[
            {
              id: 1,
              rule: 'Reavaliação Obrigatória',
              description: 'Paciente com dor > 7 sem reavaliação em 1h',
              case: 'João Silva - UTI',
              severity: 'high' as const,
              timestamp: '15:30',
              status: 'active'
            },
            {
              id: 2,
              rule: 'Documentação Completa',
              description: 'Faltam dados na escala de dor',
              case: 'Maria Santos - Enfermaria',
              severity: 'medium' as const,
              timestamp: '14:45',
              status: 'resolved'
            },
            {
              id: 3,
              rule: 'Tempo de Resposta',
              description: 'Demora na administração de analgésico',
              case: 'Pedro Costa - Cirúrgico',
              severity: 'low' as const,
              timestamp: '13:20',
              status: 'investigating'
            }
          ].map((violation) => (
            <div key={violation.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      violation.severity === 'high' ? 'bg-red-100 text-red-800' :
                      violation.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {violation.rule}
                    </span>
                    <span className="text-xs text-gray-500">{violation.timestamp}</span>
                  </div>
                  
                  <p className="text-sm text-gray-900 mb-1">{violation.description}</p>
                  <p className="text-xs text-gray-600">{violation.case}</p>
                </div>
                
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  violation.status === 'active' ? 'bg-red-100 text-red-800' :
                  violation.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {violation.status === 'active' ? 'Ativa' :
                   violation.status === 'resolved' ? 'Resolvida' :
                   'Investigando'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPerformancePanel = () => (
    <div className="space-y-6">
      {/* Métricas de Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Tempo Médio de Ciclo</h3>
            <Clock className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">4.2h</div>
          <div className="flex items-center">
            <span className="text-sm text-green-600 font-medium">-8.1%</span>
            <span className="text-xs text-gray-500 ml-1">vs. semana anterior</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Tempo de Espera</h3>
            <Clock className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">1.8h</div>
          <div className="flex items-center">
            <span className="text-sm text-red-600 font-medium">+12.3%</span>
            <span className="text-xs text-gray-500 ml-1">vs. semana anterior</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Taxa de Throughput</h3>
            <BarChart3 className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">18.3</div>
          <div className="text-xs text-gray-500">casos/dia</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Utilização Recursos</h3>
            <Users className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">78%</div>
          <div className="flex items-center">
            <span className="text-sm text-green-600 font-medium">+5.2%</span>
            <span className="text-xs text-gray-500 ml-1">vs. semana anterior</span>
          </div>
        </div>
      </div>

      {/* Análise de Gargalos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Análise de Gargalos
        </h3>
        
        <div className="space-y-4">
          {[
            {
              activity: ActivityType.pain_assessment_initial,
              waitingTime: '45 min',
              impact: 'high',
              cases: 23,
              suggestion: 'Adicionar mais recursos durante 14h-16h'
            },
            {
              activity: ActivityType.medication_administration,
              waitingTime: '28 min',
              impact: 'medium',
              cases: 16,
              suggestion: 'Otimizar processo de preparação de medicamentos'
            },
            {
              activity: ActivityType.specialist_referral,
              waitingTime: '2.3 h',
              impact: 'low',
              cases: 8,
              suggestion: 'Implementar sistema de agendamento automático'
            }
          ].map((bottleneck, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">{bottleneck.activity}</h4>
                  <p className="text-sm text-gray-600">
                    Tempo de espera médio: <span className="font-medium">{bottleneck.waitingTime}</span>
                  </p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    bottleneck.impact === 'high' ? 'bg-red-100 text-red-800' :
                    bottleneck.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {bottleneck.impact === 'high' ? 'Alto Impacto' :
                     bottleneck.impact === 'medium' ? 'Médio Impacto' :
                     'Baixo Impacto'}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">{bottleneck.cases} casos</p>
                </div>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Sugestão:</strong> {bottleneck.suggestion}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPredictionPanel = () => (
    <div className="space-y-6">
      {/* Modelos Preditivos Ativos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Predição de Desfecho</h3>
            <Brain className="h-6 w-6 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-blue-600 mb-2">92%</div>
          <p className="text-sm text-gray-600">Acurácia do modelo</p>
          <div className="mt-4">
            <div className="text-sm text-gray-700 mb-2">Próximas 24h:</div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Sucesso esperado</span>
                <span className="text-green-600">78%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Complicações</span>
                <span className="text-red-600">12%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Necessita escalation</span>
                <span className="text-yellow-600">10%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Previsão de Demanda</h3>
            <TrendingUp className="h-6 w-6 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-green-600 mb-2">+15%</div>
          <p className="text-sm text-gray-600">Próxima semana</p>
          <div className="mt-4">
            <div className="text-sm text-gray-700 mb-2">Recursos necessários:</div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Enfermeiros</span>
                <span>+2 por turno</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Medicamentos</span>
                <span>+20%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Leitos</span>
                <span>+3 unidades</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Detecção de Anomalias</h3>
            <AlertTriangle className="h-6 w-6 text-orange-500" />
          </div>
          <div className="text-2xl font-bold text-orange-600 mb-2">5</div>
          <p className="text-sm text-gray-600">Anomalias detectadas hoje</p>
          <div className="mt-4">
            <div className="text-sm text-gray-700 mb-2">Tipos identificados:</div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Padrão incomum</span>
                <span>3</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Outlier temporal</span>
                <span>1</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Desvio de recurso</span>
                <span>1</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recomendações Inteligentes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <Zap className="h-5 w-5 inline-block mr-2" />
          Recomendações Inteligentes
        </h3>
        
        <div className="space-y-4">
          {[
            {
              id: 1,
              priority: 'high' as const,
              title: 'Otimizar Alocação de Recursos - Turno da Tarde',
              description: 'O modelo prevê sobrecarga de 40% no turno vespertino. Recomendação: adicionar 1 enfermeiro especializado.',
              impact: 'Redução de 25% no tempo de espera',
              confidence: 89,
              timeframe: 'Próximas 48h'
            },
            {
              id: 2,
              priority: 'medium' as const,
              title: 'Revisar Protocolo para Dor Crônica',
              description: 'Pacientes com dor crônica mostram padrão divergente do protocolo padrão com melhores resultados.',
              impact: 'Melhoria de 15% na satisfação',
              confidence: 76,
              timeframe: 'Próxima semana'
            },
            {
              id: 3,
              priority: 'low' as const,
              title: 'Implementar Intervenção Preventiva',
              description: 'Modelo identifica que certos pacientes podem se beneficiar de intervenção não-farmacológica prévia.',
              impact: 'Redução de 30% uso de opioides',
              confidence: 68,
              timeframe: 'Próximo mês'
            }
          ].map((recommendation) => (
            <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                      recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {recommendation.priority === 'high' ? 'Alta Prioridade' :
                       recommendation.priority === 'medium' ? 'Média Prioridade' :
                       'Baixa Prioridade'}
                    </span>
                    <span className="text-xs text-gray-500">Confiança: {recommendation.confidence}%</span>
                  </div>
                  
                  <h4 className="font-medium text-gray-900 mb-1">{recommendation.title}</h4>
                  <p className="text-sm text-gray-600 mb-2">{recommendation.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="bg-blue-50 p-2 rounded">
                      <p className="text-xs text-blue-800 font-medium">Impacto Esperado</p>
                      <p className="text-xs text-blue-700">{recommendation.impact}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded">
                      <p className="text-xs text-purple-800 font-medium">Prazo</p>
                      <p className="text-xs text-purple-700">{recommendation.timeframe}</p>
                    </div>
                  </div>
                </div>
                
                <div className="ml-4 space-y-2">
                  <button className="w-full px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">
                    Aceitar
                  </button>
                  <button className="w-full px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50">
                    Detalhes
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Process Mining - Protocolo de Dor</h1>
                <p className="text-sm text-gray-600">Sistema Inteligente de Análise de Processos</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsRealTimeActive(!isRealTimeActive)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isRealTimeActive 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isRealTimeActive ? (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pausar Tempo Real
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Iniciar Tempo Real
                  </>
                )}
              </button>
              
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
              </button>
              
              <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {mockKPIs.map((kpi, index) => renderKPICard(kpi, index))}
        </div>

        {/* Alertas Críticos */}
        {alerts.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <h3 className="text-sm font-medium text-red-800">Alertas Críticos Ativos</h3>
            </div>
            <div className="mt-2 space-y-1">
              {alerts.slice(0, 3).map((alert) => (
                <p key={alert.id} className="text-sm text-red-700">
                  • {alert.title}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Tabs de Navegação */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'discovery', label: 'Descoberta de Processos', icon: Search },
                { key: 'conformance', label: 'Análise de Conformidade', icon: Target },
                { key: 'performance', label: 'Performance', icon: BarChart3 },
                { key: 'prediction', label: 'Analytics Preditivo', icon: Brain }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    selectedTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {selectedTab === 'discovery' && renderProcessDiscoveryPanel()}
            {selectedTab === 'conformance' && renderConformancePanel()}
            {selectedTab === 'performance' && renderPerformancePanel()}
            {selectedTab === 'prediction' && renderPredictionPanel()}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo dos Dados</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{totalCases}</p>
              <p className="text-sm text-gray-600">Total de Casos</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{eventLogs.length}</p>
              <p className="text-sm text-gray-600">Eventos Processados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{uniqueActivities}</p>
              <p className="text-sm text-gray-600">Atividades Únicas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {isRealTimeActive ? 'LIVE' : 'PAUSED'}
              </p>
              <p className="text-sm text-gray-600">Status do Sistema</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProcessMiningDashboard;
